'use client';

/**
 * DemoSceneProgressionCard — OODA-L scene breadcrumb for the demo canvas.
 *
 * Horizontal pill row: SUBMIT → ACT(s) → LEARN with per-scene success
 * status. Width-weighted by duration so the eye lands on the heavy
 * scenes (typically the LLM-mediated ACT). Universal across cases —
 * paints from any parent test_run CV's :HAS_MESSAGE chain.
 *
 * Reads `StepSlice[]` off `test_run_view`. React Query dedupes the
 * fetch with sibling cards (header card, etc.) — one BFF call, many
 * cards consuming slices.
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain canvas
 */

import { useQuery } from '@tanstack/react-query';
import { Check, X, ArrowRight } from 'lucide-react';
import { api } from '@/lib/api/client';
import { useDemoAskStore } from '@/lib/stores/demo-ask-store';
import { useCardTypography } from '@/lib/design/card-chrome';
import { cn } from '@/lib/utils/cn';
import type { TestRunView, StepSlice } from './demo-test-run-types';


export function DemoSceneProgressionCard() {
  const T = useCardTypography();
  const simulationId = useDemoAskStore((s) => s.testRun?.simulation_id ?? null);

  const { data } = useQuery<TestRunView>({
    queryKey: ['test_run_view', simulationId],
    queryFn: async () => {
      const res = await api.getStrategyData<TestRunView>('test_run_view', {
        simulation_id: simulationId,
      });
      if (!res.success) throw new Error(res.error || 'test_run_view failed');
      return res.data;
    },
    enabled: Boolean(simulationId),
    staleTime: 30_000,
  });

  if (!simulationId || !data) return null;

  const scenes = data.scenes;

  return (
    <div className="bg-card/30 border border-border rounded-2xl px-5 py-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div className={cn(T.body, 'text-sm font-medium')}>Scene progression</div>
        <div className={cn(T.metadata, 'text-muted-foreground text-xs font-mono')}>
          {data.parent_sid} · {(data.total_ms / 1000).toFixed(1)}s
        </div>
      </div>
      <div className="flex items-center gap-1">
        {scenes.map((s, idx) => (
          <ScenePillRow key={`${s.seq}-${s.subject}`} scene={s} isLast={idx === scenes.length - 1} />
        ))}
      </div>
    </div>
  );
}


interface ScenePillRowProps {
  scene: StepSlice;
  isLast: boolean;
}

function ScenePillRow({ scene, isLast }: ScenePillRowProps) {
  // Width weighting — heavy scenes (LLM-mediated) get more visual area.
  // Linear interpolation against ms with a floor and a ceiling.
  const flex = Math.max(1, Math.min(8, Math.round(1 + scene.duration_ms / 800)));

  const isOk = scene.success;
  const label = scene.subject === 'ACT' && scene.capability
    ? `ACT — ${scene.capability}`
    : scene.subject;

  return (
    <>
      <div
        style={{ flex }}
        className={cn(
          'px-2.5 py-1.5 rounded-lg text-xs flex items-center gap-1.5 truncate',
          isOk
            ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
            : 'bg-destructive/10 text-destructive',
        )}
        title={`${label} · ${scene.duration_ms} ms${scene.cognition_mode ? ` · cognition: ${scene.cognition_mode}` : ''}`}
      >
        {isOk ? <Check className="w-3.5 h-3.5 shrink-0" /> : <X className="w-3.5 h-3.5 shrink-0" />}
        <span className="truncate font-mono">{label}</span>
        {scene.cognition_mode === 'llm' && (
          <span className="ml-auto text-[9px] font-mono opacity-70 uppercase tracking-wider shrink-0">
            llm
          </span>
        )}
      </div>
      {!isLast && (
        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/50 shrink-0" />
      )}
    </>
  );
}
